import { useTranslation } from 'react-i18next';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { PartnerDTO } from 'support/types';
import { AlertSection } from 'components/Display/Alert';
import { Alert } from 'components/Display/Alert/Alert';
import { AnnotationNavigationActions } from 'components/WebEDI/AnnotationNavigationActions/AnnotationNavigationActions';

import { useOtherPartnerAnnotationsCopy } from '../hooks/useOtherPartnerCopy';
import { AnnotationsLabel } from '../components/AnnotationsLabel';
import { LoadingErrors } from '../components/LoadingErrors';

type Props = {
  isCurrentPartnerReceiver?: boolean;
  senderPartner?: PartnerDTO | null;
  receiverPartner?: PartnerDTO | null;
  numberOfErrors: number;
  isLoading: boolean;
};

export const OtherPartnerResolverAnnotationsPreset = ({
  isCurrentPartnerReceiver,
  senderPartner,
  receiverPartner,
  numberOfErrors,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const totalCycleableItems = service.useNumberOfCycleableItems();
  const { title, description } = useOtherPartnerAnnotationsCopy(
    { isCurrentPartnerReceiver, senderPartner, receiverPartner },
    numberOfErrors,
    isLoading,
  );

  const moveToNext = () => {
    track('webedi:next_error', { nextError: service.getCurrentItem() });
    service.moveToNextItem();
  };
  const moveToPrevious = () => {
    track('webedi:previous_error', { previousError: service.getPreviousItem() });
    service.moveToPreviousItem();
  };

  return (
    <Alert withoutIcon severity="neutral" fullWidth className="max-h-72 overflow-y-auto">
      <div className="flex w-full items-center justify-between">
        <AlertSection severity="neutral" title={title}>
          {description}
        </AlertSection>
        {isLoading ? (
          <LoadingErrors />
        ) : (
          <AnnotationNavigationActions
            leadingContent={
              <AnnotationsLabel
                variant="error"
                label={t('webedi:viewDocument.errorBanner.badge', { count: numberOfErrors, numberOfErrors })}
              />
            }
            totalNumberOfAnnotations={totalCycleableItems}
            onPrev={moveToPrevious}
            onNext={moveToNext}
            prevAnalyticsId="webedi:previous_error"
            nextAnalyticsId="webedi:next_error"
            onFocus={service.resetFocus}
            focusAnalyticsId="webedi:errors_focus_first"
            focusLabel={t('webedi:viewDocument.errorBanner.focusOnError')}
          />
        )}
      </div>
    </Alert>
  );
};
